/**
 * A aba "Tratamento" do cliente (#811, fase 4): o tratamento cadastrado no
 * paciente e o valor negociado.
 *
 * ── Só com a clínica ligada ──────────────────────────────────────────────
 *
 * A aba só existe com a funcionalidade "clinica" ligada; sem ela, a API
 * responde 404 e a tela nem pergunta.
 *
 * ── O SISAC manda o nome e o valor de tabela ─────────────────────────────
 *
 * O tratamento é escolhido da lista do SISAC, e o nome e o valor de tabela
 * não se editam aqui. O valor negociado começa igual ao de tabela e se
 * ajusta; cada ajuste pede o motivo (opcional) e fica no histórico, com quem
 * e quando. O valor de tabela aparece riscado ao lado do negociado quando há
 * desconto, e o desconto em reais e em porcentagem.
 *
 * ── A oportunidade ───────────────────────────────────────────────────────
 *
 * Ligado a uma oportunidade do paciente, o valor negociado é o valor dela no
 * funil. A escolha mostra só as oportunidades que este cliente tem no 360.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ErroDaApi,
  type OportunidadeDo360,
  type SituacaoDoTratamentoDaApi,
  type TratamentoDoPaciente,
  type MudancaDeCampos,
} from '@cliqcrm/contracts';
import {
  CamposEditaveis,
  obrigatoriosVazios,
  ValoresDosCampos,
} from '@/entities/campos/campo-personalizado';
import {
  useApi,
  useCamposPersonalizados,
  useCatalogoDeTratamentos,
  useOpcoesDaConsulta,
  useTratamentosDoPaciente,
} from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import {
  Botao,
  Campo,
  CampoArea,
  CampoSelecao,
  Chip,
  Dialogo,
  Icone,
  MenuAcoes,
  Situacao,
} from '@/shared/ui';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import {
  camposDoCorpo,
  corpoParaCriar,
  desconto,
  emReais,
  fatosDo,
  FORMULARIO_VAZIO,
  formularioDe,
  NOMES_DA_SITUACAO,
  paraCampo,
  paraMicros,
  type FormularioDoTratamento,
} from '../lib/tratamento-do-paciente';
import './aba-do-tratamento.css';

const CONFLITO = 'Outra pessoa mudou este tratamento enquanto você editava. Recarregue e refaça.';

function recado(erro: unknown, generica: string): string {
  if (erro instanceof ErroDaApi && erro.codigo === 'CONFLITO_DE_VERSAO') return CONFLITO;
  return recadoDeErro(erro, { generica });
}

export function TratamentosNoCliente({
  clienteId,
  podeEditar,
  oportunidades,
}: {
  clienteId: string;
  podeEditar: boolean;
  /** As oportunidades do cliente no 360: as que o tratamento pode levar o valor. */
  oportunidades: readonly OportunidadeDo360[];
}): ReactNode {
  const consulta = useTratamentosDoPaciente(clienteId);
  const [cadastrando, definirCadastrando] = useState(false);

  if (consulta.isPending) {
    return <Carregando forma="lista" linhas={2} rotulo="Carregando os tratamentos" />;
  }
  if (consulta.isError) {
    return (
      <Erro
        erro={consulta.error}
        titulo="Não foi possível abrir os tratamentos"
        aoRepetir={() => void consulta.refetch()}
      />
    );
  }

  const tratamentos = consulta.data.itens;
  return (
    <div className="trat-cli">
      {podeEditar && (
        <div className="trat-cli__barra">
          <Botao variante="primario" onClick={() => definirCadastrando(true)}>
            <Icone nome="criar" />
            Novo tratamento
          </Botao>
        </div>
      )}

      {cadastrando && (
        <CadastroDoTratamento
          clienteId={clienteId}
          oportunidades={oportunidades}
          aoFechar={() => definirCadastrando(false)}
        />
      )}

      {tratamentos.length === 0 && <p className="trat-cli__vazio">Nenhum tratamento cadastrado.</p>}

      {tratamentos.length > 0 && (
        <ul className="trat-cli__lista" aria-label="Tratamentos do paciente">
          {tratamentos.map((tratamento) => (
            <CartaoDoTratamento
              key={tratamento.id}
              clienteId={clienteId}
              tratamento={tratamento}
              podeEditar={podeEditar}
              oportunidades={oportunidades}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CartaoDoTratamento({
  clienteId,
  tratamento,
  podeEditar,
  oportunidades,
}: {
  clienteId: string;
  tratamento: TratamentoDoPaciente;
  podeEditar: boolean;
  oportunidades: readonly OportunidadeDo360[];
}): ReactNode {
  const [modo, definirModo] = useState<'ver' | 'valor' | 'dados'>('ver');
  const [historico, definirHistorico] = useState(false);
  const camposDoTratamento = useCamposPersonalizados('tratamento').data?.itens ?? [];
  const tabela = tratamento.valor_tabela.quantia_micros;
  const negociado = tratamento.valor_negociado.quantia_micros;
  const moeda = tratamento.valor_negociado.moeda;
  const descontoDele = desconto(tabela, negociado);
  const oportunidade = oportunidades.find((o) => o.id === tratamento.oportunidade_id);

  return (
    <li className="trat-cli__item">
      <div className="trat-cli__cabeca">
        <h3 className="trat-cli__nome">{tratamento.nome}</h3>
        <Situacao
          texto={NOMES_DA_SITUACAO[tratamento.situacao]}
          tom={tratamento.situacao === 'ativo' ? 'ok' : 'neutro'}
        />
        {podeEditar && modo === 'ver' && (
          <span className="trat-cli__menu">
            <MenuAcoes
              rotulo={`Ações do tratamento ${tratamento.nome}`}
              itens={[
                { rotulo: 'Ajustar valor', aoClicar: () => definirModo('valor') },
                { rotulo: 'Editar dados', aoClicar: () => definirModo('dados') },
              ]}
            />
          </span>
        )}
      </div>

      <span className="trat-cli__detalhe">
        {[`código ${tratamento.codigo}`, ...fatosDo(tratamento)].join(' · ')}
      </span>

      <div className="trat-cli__valores">
        <div className="trat-cli__valor">
          <span className="trat-cli__rotulo">Valor negociado</span>
          <strong className="trat-cli__negociado">{emReais(negociado, moeda)}</strong>
        </div>
        <div className="trat-cli__valor">
          <span className="trat-cli__rotulo">Tabela do SISAC</span>
          <span
            className={
              descontoDele ? 'trat-cli__tabela trat-cli__tabela--riscada' : 'trat-cli__tabela'
            }
          >
            {emReais(tabela, moeda)}
          </span>
        </div>
        {descontoDele && (
          <Chip tom="atencao">
            Desconto de {emReais(descontoDele.micros, moeda)} ({descontoDele.porcento}%)
          </Chip>
        )}
        {tratamento.oportunidade_id && (
          <span className="trat-cli__funil">
            <Icone nome="funil" />
            {oportunidade?.titulo
              ? `Valor de "${oportunidade.titulo}" no funil`
              : 'Valor da oportunidade no funil'}
          </span>
        )}
      </div>

      {modo === 'valor' && (
        <AjusteDeValor
          clienteId={clienteId}
          tratamento={tratamento}
          aoFechar={() => definirModo('ver')}
        />
      )}
      {modo === 'dados' && (
        <EdicaoDoTratamento
          clienteId={clienteId}
          tratamento={tratamento}
          aoFechar={() => definirModo('ver')}
        />
      )}

      {/* Os campos personalizados do tratamento (#568), na leitura. */}
      {modo === 'ver' && (
        <ValoresDosCampos campos={camposDoTratamento} valores={tratamento.campos} />
      )}

      {tratamento.ajustes.length > 0 && (
        <div className="trat-cli__historico">
          <Botao
            miudo
            variante="fantasma"
            aria-expanded={historico}
            onClick={() => definirHistorico(!historico)}
          >
            {historico ? 'Esconder' : 'Ver'} o histórico do valor ({tratamento.ajustes.length})
          </Botao>
          {historico && (
            <ol className="trat-cli__ajustes" aria-label={`Ajustes do valor de ${tratamento.nome}`}>
              {[...tratamento.ajustes].reverse().map((ajuste) => (
                <li key={ajuste.ajustado_em} className="trat-cli__ajuste">
                  <span>
                    {emReais(ajuste.valor_anterior.quantia_micros, moeda)} →{' '}
                    <strong>{emReais(ajuste.valor_novo.quantia_micros, moeda)}</strong>
                  </span>
                  {ajuste.motivo && <span className="trat-cli__motivo">{ajuste.motivo}</span>}
                  <span className="trat-cli__quem">
                    {ajuste.ajustado_por?.nome ?? 'alguém que não está mais na equipe'},{' '}
                    {new Date(ajuste.ajustado_em).toLocaleString('pt-BR', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </li>
  );
}

function AjusteDeValor({
  clienteId,
  tratamento,
  aoFechar,
}: {
  clienteId: string;
  tratamento: TratamentoDoPaciente;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [valor, definirValor] = useState(paraCampo(tratamento.valor_negociado.quantia_micros));
  const [motivo, definirMotivo] = useState('');
  const micros = paraMicros(valor);
  const prefixo = `ajuste-${tratamento.id}`;

  const salvar = useMutation({
    mutationFn: () =>
      api!.ajustarValorDoTratamento({
        caminho: { id: clienteId, tratamentoId: tratamento.id },
        corpo: {
          valor_negociado_micros: micros!,
          motivo: motivo.trim() === '' ? null : motivo.trim(),
          versao: tratamento.versao,
        },
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.tratamentosDoPaciente(clienteId) });
      // O valor pode ter ido para a oportunidade: o 360 relê o funil.
      void cache.invalidateQueries({ queryKey: chaves.cliente360(clienteId) });
      aoFechar();
    },
  });
  const descontoNovo =
    micros === null ? null : desconto(tratamento.valor_tabela.quantia_micros, micros);

  return (
    <form
      className="trat-cli__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (micros !== null) salvar.mutate();
      }}
    >
      <fieldset className="trat-cli__grupo">
        <legend>Ajustar o valor negociado</legend>
        <Campo
          id={`${prefixo}-valor`}
          rotulo="Valor negociado (R$)"
          inputMode="decimal"
          value={valor}
          erro={micros === null ? 'Escreva o valor, como 4.800,00.' : undefined}
          ajuda={
            descontoNovo
              ? `Desconto de ${emReais(descontoNovo.micros)} (${descontoNovo.porcento}%) sobre a tabela.`
              : `Tabela do SISAC: ${emReais(tratamento.valor_tabela.quantia_micros)}.`
          }
          onChange={(e) => definirValor(e.currentTarget.value)}
        />
        <CampoArea
          id={`${prefixo}-motivo`}
          rotulo="Motivo"
          maxLength={500}
          rows={2}
          ajuda="Opcional. Fica no histórico do valor."
          value={motivo}
          onChange={(e) => definirMotivo(e.currentTarget.value)}
        />
      </fieldset>
      {salvar.isError && (
        <p className="trat-cli__erro" role="alert">
          {recado(salvar.error, 'Não foi possível ajustar o valor.')}
        </p>
      )}
      <div className="trat-cli__acoes">
        <Botao
          variante="primario"
          type="submit"
          desabilitado={micros === null || salvar.isPending}
          motivo={salvar.isPending ? 'Salvando…' : 'Escreva o valor.'}
        >
          Salvar valor
        </Botao>
        <Botao variante="fantasma" type="button" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

function EdicaoDoTratamento({
  clienteId,
  tratamento,
  aoFechar,
}: {
  clienteId: string;
  tratamento: TratamentoDoPaciente;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [formulario, definirFormulario] = useState(formularioDe(tratamento));
  const [situacao, definirSituacao] = useState<SituacaoDoTratamentoDaApi>(tratamento.situacao);
  const prefixo = `tratamento-${tratamento.id}`;
  const camposDoTratamento = useCamposPersonalizados('tratamento');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});

  const salvar = useMutation({
    mutationFn: () =>
      api!.editarTratamentoDoPaciente({
        caminho: { id: clienteId, tratamentoId: tratamento.id },
        corpo: {
          ...camposDoCorpo(formulario),
          situacao,
          versao: tratamento.versao,
          ...(Object.keys(mudancaDosCampos).length > 0 ? { campos: mudancaDosCampos } : {}),
        },
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.tratamentosDoPaciente(clienteId) });
      aoFechar();
    },
  });

  return (
    <form
      className="trat-cli__formulario"
      onSubmit={(evento) => {
        evento.preventDefault();
        salvar.mutate();
      }}
    >
      <CamposDoTratamento prefixo={prefixo} formulario={formulario} aoMudar={definirFormulario}>
        <CampoSelecao
          id={`${prefixo}-situacao`}
          rotulo="Situação"
          value={situacao}
          onChange={(e) => definirSituacao(e.currentTarget.value as SituacaoDoTratamentoDaApi)}
        >
          {(Object.keys(NOMES_DA_SITUACAO) as SituacaoDoTratamentoDaApi[]).map((chave) => (
            <option key={chave} value={chave}>
              {NOMES_DA_SITUACAO[chave]}
            </option>
          ))}
        </CampoSelecao>
      </CamposDoTratamento>
      <CamposEditaveis
        id={`${prefixo}-campo`}
        campos={camposDoTratamento.data?.itens ?? []}
        valores={tratamento.campos}
        mudanca={mudancaDosCampos}
        aoMudar={definirMudancaDosCampos}
      />
      {salvar.isError && (
        <p className="trat-cli__erro" role="alert">
          {recado(salvar.error, 'Não foi possível salvar o tratamento.')}
        </p>
      )}
      <div className="trat-cli__acoes">
        <Botao variante="primario" type="submit" desabilitado={salvar.isPending} motivo="Salvando…">
          Salvar
        </Botao>
        <Botao variante="fantasma" type="button" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>
    </form>
  );
}

function CadastroDoTratamento({
  clienteId,
  oportunidades,
  aoFechar,
}: {
  clienteId: string;
  oportunidades: readonly OportunidadeDo360[];
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const catalogo = useCatalogoDeTratamentos(true);
  const ativos = (catalogo.data?.itens ?? []).filter((item) => item.ativo);
  const [tratamentoId, definirTratamentoId] = useState('');
  const [valor, definirValor] = useState('');
  const [motivo, definirMotivo] = useState('');
  const [oportunidadeId, definirOportunidadeId] = useState('');
  const [formulario, definirFormulario] = useState<FormularioDoTratamento>(FORMULARIO_VAZIO);
  const escolhido = ativos.find((item) => item.id === tratamentoId);
  const tabela = escolhido?.valor_tabela.quantia_micros ?? 0;
  const micros = valor.trim() === '' ? tabela : paraMicros(valor);
  const descontoNovo = escolhido && micros !== null ? desconto(tabela, micros) : null;
  const ligaveis = oportunidades.filter((o) => o.id !== null);
  // Os campos personalizados do tratamento (#568): os obrigatórios entram no cadastro.
  const camposDoTratamento = useCamposPersonalizados('tratamento');
  const [mudancaDosCampos, definirMudancaDosCampos] = useState<MudancaDeCampos>({});
  const faltando = obrigatoriosVazios(camposDoTratamento.data?.itens ?? [], {}, mudancaDosCampos);

  const criar = useMutation({
    mutationFn: () =>
      api!.criarTratamentoDoPaciente({
        caminho: { id: clienteId },
        corpo: corpoParaCriar({
          formulario,
          tratamentoId,
          oportunidadeId,
          valorTabelaMicros: tabela,
          valorNegociadoMicros: micros,
          motivo,
          campos: mudancaDosCampos,
        }),
        // Chave por tentativa: o segundo clique não cria o tratamento duas vezes.
        idempotencyKey:
          globalThis.crypto?.randomUUID?.() ?? `tratamento-${Date.now()}-${Math.random()}`,
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.tratamentosDoPaciente(clienteId) });
      void cache.invalidateQueries({ queryKey: chaves.cliente360(clienteId) });
      aoFechar();
    },
  });
  const pronto = escolhido !== undefined && micros !== null && faltando.length === 0;

  return (
    <Dialogo
      titulo="Novo tratamento"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            type="submit"
            form="tratamento-novo"
            desabilitado={!pronto || criar.isPending}
            motivo={
              criar.isPending
                ? 'Cadastrando…'
                : escolhido === undefined
                  ? 'Escolha o tratamento.'
                  : `Preencha ${faltando.map((campo) => campo.nome).join(', ')}.`
            }
          >
            {criar.isPending ? 'Cadastrando…' : 'Cadastrar tratamento'}
          </Botao>
        </>
      }
    >
      <form
        id="tratamento-novo"
        className="trat-cli__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (pronto) criar.mutate();
        }}
      >
        <fieldset className="trat-cli__grupo">
          <legend>Tratamento e valor</legend>
          <div className="trat-cli__largo">
            <CampoSelecao
              id="tratamento-novo-item"
              rotulo="Tratamento"
              value={tratamentoId}
              disabled={ativos.length === 0}
              ajuda={
                ativos.length === 0
                  ? 'A lista vem do SISAC e ainda está vazia.'
                  : 'Da lista do SISAC, com o valor de tabela.'
              }
              onChange={(e) => {
                definirTratamentoId(e.currentTarget.value);
                definirValor('');
              }}
            >
              <option value="">{ativos.length === 0 ? 'Nenhum tratamento' : 'Escolha'}</option>
              {ativos.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.nome} · {emReais(item.valor_tabela.quantia_micros, item.valor_tabela.moeda)}
                </option>
              ))}
            </CampoSelecao>
          </div>
          <Campo
            id="tratamento-novo-valor"
            rotulo="Valor negociado (R$)"
            inputMode="decimal"
            placeholder={escolhido ? paraCampo(tabela) : ''}
            value={valor}
            disabled={!escolhido}
            erro={micros === null ? 'Escreva o valor, como 4.800,00.' : undefined}
            ajuda={
              descontoNovo
                ? `Desconto de ${emReais(descontoNovo.micros)} (${descontoNovo.porcento}%) sobre a tabela.`
                : 'Vazio vale o valor de tabela.'
            }
            onChange={(e) => definirValor(e.currentTarget.value)}
          />
          <CampoSelecao
            id="tratamento-novo-oportunidade"
            rotulo="Oportunidade"
            value={oportunidadeId}
            disabled={ligaveis.length === 0}
            ajuda="Opcional. O valor negociado vira o valor dela no funil."
            onChange={(e) => definirOportunidadeId(e.currentTarget.value)}
          >
            <option value="">{ligaveis.length === 0 ? 'Nenhuma oportunidade' : 'Nenhuma'}</option>
            {ligaveis.map((o) => (
              <option key={o.id} value={o.id!}>
                {o.titulo ?? 'Oportunidade sem título'}
              </option>
            ))}
          </CampoSelecao>
          {descontoNovo && (
            <div className="trat-cli__largo">
              <CampoArea
                id="tratamento-novo-motivo"
                rotulo="Motivo do desconto"
                maxLength={500}
                rows={2}
                ajuda="Opcional. Fica no histórico do valor."
                value={motivo}
                onChange={(e) => definirMotivo(e.currentTarget.value)}
              />
            </div>
          )}
        </fieldset>
        <CamposDoTratamento
          prefixo="tratamento-novo"
          formulario={formulario}
          aoMudar={definirFormulario}
        />
        <CamposEditaveis
          id="tratamento-novo-campo"
          campos={camposDoTratamento.data?.itens ?? []}
          valores={{}}
          mudanca={mudancaDosCampos}
          aoMudar={definirMudancaDosCampos}
        />
      </form>
      {criar.isError && (
        <p className="trat-cli__erro" role="alert">
          {recado(criar.error, 'Não foi possível cadastrar o tratamento.')}
        </p>
      )}
    </Dialogo>
  );
}

function CamposDoTratamento({
  prefixo,
  formulario,
  aoMudar,
  children,
}: {
  prefixo: string;
  formulario: FormularioDoTratamento;
  aoMudar: (formulario: FormularioDoTratamento) => void;
  children?: ReactNode;
}): ReactNode {
  const opcoes = useOpcoesDaConsulta(true);
  const medicos = opcoes.data?.medicos ?? [];
  const mudar = <C extends keyof FormularioDoTratamento>(campo: C, valor: string) =>
    aoMudar({ ...formulario, [campo]: valor });
  return (
    <fieldset className="trat-cli__grupo">
      <legend>Datas, profissional e pagamento</legend>
      <Campo
        id={`${prefixo}-fechado`}
        rotulo="Fechado em"
        type="date"
        value={formulario.fechadoEm}
        onChange={(e) => mudar('fechadoEm', e.currentTarget.value)}
      />
      <Campo
        id={`${prefixo}-inicio`}
        rotulo="Início do tratamento"
        type="date"
        value={formulario.inicioEm}
        onChange={(e) => mudar('inicioEm', e.currentTarget.value)}
      />
      <CampoSelecao
        id={`${prefixo}-medico`}
        rotulo="Profissional"
        value={formulario.medicoId}
        disabled={medicos.length === 0 && formulario.medicoId === ''}
        onChange={(e) => mudar('medicoId', e.currentTarget.value)}
      >
        <option value="">{medicos.length === 0 ? 'Nenhum profissional' : 'Nenhum'}</option>
        {medicos.map((medico) => (
          <option key={medico.id} value={medico.id}>
            {medico.nome}
          </option>
        ))}
      </CampoSelecao>
      <Campo
        id={`${prefixo}-pagamento`}
        rotulo="Forma de pagamento"
        maxLength={120}
        value={formulario.formaPagamento}
        onChange={(e) => mudar('formaPagamento', e.currentTarget.value)}
      />
      <Campo
        id={`${prefixo}-parcelas`}
        rotulo="Parcelas"
        inputMode="numeric"
        value={formulario.parcelas}
        onChange={(e) => mudar('parcelas', e.currentTarget.value)}
      />
      <Campo
        id={`${prefixo}-sessoes`}
        rotulo="Sessões previstas"
        inputMode="numeric"
        value={formulario.sessoes}
        onChange={(e) => mudar('sessoes', e.currentTarget.value)}
      />
      {children}
    </fieldset>
  );
}
